'use client';
import React, { useRef, useEffect, useState } from 'react';
import { ButtonFragmentFragment, TypeButton } from '@fm-nx/contentful-types';
import { useStickyButton, StickyButtonConfig } from '../sticky-button-provider/sticky-button-provider';
import LinkWrapper from '../link-wrapper/link-wrapper';
import { PageContentTypes } from '@fm-nx/constants';
import classNames from 'classnames';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { withSafeCrossOriginIframes, patchScrollTriggerRefresh } from '../__constants__/gsap-safe-init';

gsap.registerPlugin(ScrollTrigger);
patchScrollTriggerRefresh();

type StickyButtonProps = {
    button: TypeButton<'WITHOUT_UNRESOLVABLE_LINKS'> | ButtonFragmentFragment;
};

export const StickyButton = ({ button }: StickyButtonProps) => {
    const ctx = useStickyButton();
    const placeholderRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    const btnProp = 'fields' in button ? button.fields : button;
    const { linkPage } = btnProp;
    const linkPageProp = linkPage && 'fields' in linkPage ? linkPage.fields : linkPage;
    const slug = linkPageProp?.slug ? linkPageProp.slug.toString() : undefined;
    const contentType = linkPage && 'fields' in linkPage ? linkPage.sys.contentType.sys.id : linkPage?.__typename;
    // SDK gives a lowercase content type id (`pageTechnology`); GraphQL gives a
    // capitalized __typename (`PageTechnology`). Normalize before matching so
    // both button shapes resolve to a page type.
    const normalizedContentType = contentType ? contentType.charAt(0).toLowerCase() + contentType.slice(1) : undefined;
    const pageType = Object.values(PageContentTypes).find(
        (value) => value === normalizedContentType
    ) as keyof typeof PageContentTypes;

    const defaultConfig: StickyButtonConfig = {
        text: btnProp.buttonText?.toString() || '',
        linkPath: btnProp.linkPath?.toString() || '#',
        buttonType: 'solid',
        slug,
        pageType,
    };

    const [activeOverride, setActiveOverride] = useState<StickyButtonConfig | null>(null);

    useEffect(() => {
        if (!ctx) return;
        ctx.setDefaultConfig(defaultConfig);
        return () => ctx.setDefaultConfig(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [button]);

    useEffect(() => {
        const placeholder = placeholderRef.current;
        const btn = buttonRef.current;
        if (!placeholder || !btn) return;

        const mm = gsap.matchMedia();

        mm.add('(min-width: 768px)', () => {
            // Lock placeholder dimensions so the layout slot doesn't collapse
            // when the button goes position:fixed. Placeholder then acts as the
            // live anchor for re-measuring on resize.
            const initialRect = btn.getBoundingClientRect();
            gsap.set(placeholder, { width: initialRect.width, height: initialRect.height });

            let deltaX = 0;
            let deltaY = 0;

            const recompute = () => {
                const rect = placeholder.getBoundingClientRect();
                // Anchor in document coords so it's invariant of current scroll.
                // Equals placeholder's viewport y at scroll=0 — the visual
                // "starting position" the animation needs to return to.
                const anchorTop = rect.top + window.scrollY;
                const anchorLeft = rect.left + window.scrollX;
                gsap.set(btn, {
                    position: 'fixed',
                    top: anchorTop,
                    left: anchorLeft,
                    right: 'auto',
                    bottom: 'auto',
                    zIndex: 50,
                });
                const targetTop = window.innerHeight - rect.height - 50;
                const targetLeft = window.innerWidth - rect.width - 50;
                deltaX = targetLeft - anchorLeft;
                deltaY = targetTop - anchorTop;
            };

            recompute();

            const moveTrigger = withSafeCrossOriginIframes(() =>
                ScrollTrigger.create({
                    trigger: document.body,
                    start: 'top top',
                    end: '+=900',
                    scrub: 0.5,
                    invalidateOnRefresh: true,
                    onRefresh: recompute,
                    onUpdate: (self) => {
                        gsap.set(btn, {
                            x: deltaX * self.progress,
                            y: deltaY * self.progress,
                        });
                    },
                })
            );

            let fadeTrigger: ScrollTrigger | undefined;
            const fadeTimeout = setTimeout(() => {
                const ctaEl =
                    document.querySelector('[data-content-type="ctaSimple"]') ||
                    document.querySelector('[data-content-type="form"]');
                if (!ctaEl) return;

                fadeTrigger = withSafeCrossOriginIframes(() =>
                    ScrollTrigger.create({
                        trigger: ctaEl,
                        start: 'top 90%',
                        end: 'top 60%',
                        scrub: true,
                        onUpdate: (self) => {
                            gsap.set(btn, { opacity: 1 - self.progress });
                        },
                    })
                );
            }, 1000);

            return () => {
                clearTimeout(fadeTimeout);
                moveTrigger.kill();
                fadeTrigger?.kill();
            };
        });

        return () => mm.revert();
    }, []);

    useEffect(() => {
        if (!ctx) return;
        if (ctx.zones.length === 0) return;

        const mm = gsap.matchMedia();

        mm.add('(min-width: 768px)', () => {
            const zoneTriggers: ScrollTrigger[] = [];

            withSafeCrossOriginIframes(() => {
                ctx.zones.forEach((zone) => {
                    const zoneEl = zone.ref.current;
                    if (!zoneEl) return;

                    const trigger = ScrollTrigger.create({
                        trigger: zoneEl,
                        start: 'top 70%',
                        end: 'bottom 30%',
                        onEnter: () => setActiveOverride(zone.config),
                        onLeave: () => setActiveOverride(null),
                        onEnterBack: () => setActiveOverride(zone.config),
                        onLeaveBack: () => setActiveOverride(null),
                    });

                    zoneTriggers.push(trigger);
                });
            });

            return () => zoneTriggers.forEach((t) => t.kill());
        });

        return () => mm.revert();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx?.zones]);

    const currentConfig = activeOverride || defaultConfig;

    const isOverride = activeOverride !== null;
    const buttonClasses = classNames('btn', {
        'btn-solid': currentConfig.buttonType === 'solid',
        'btn-outline-dark': currentConfig.buttonType === 'outline-dark',
        'btn-outline-white': currentConfig.buttonType === 'outline-white',
        'bg-white': isOverride && currentConfig.buttonType === 'outline-dark',
    });

    return (
        <div ref={placeholderRef}>
            <div ref={buttonRef}>
                <LinkWrapper href={currentConfig.linkPath} slug={currentConfig.slug} pageType={currentConfig.pageType}>
                    <span className={`${buttonClasses} transition-all duration-300`}>
                        <span className="pointer-events-none">{currentConfig.text}</span>
                    </span>
                </LinkWrapper>
            </div>
        </div>
    );
};

export default StickyButton;
